/**
 * `/my/tracking` — the caller's assigned projects (PRD §4.6).
 */

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useMyProjects } from "@/lib/tracking";

export function MyTrackingPage() {
  const { t } = useTranslation();
  const projects = useMyProjects();

  return (
    <div>
      <h1 className="text-3xl font-display">{t("myTracking.heading")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("myTracking.intro")}</p>

      {projects.isLoading && <Skeleton className="mt-8 h-32 w-full rounded-xl" />}

      {!projects.isLoading && (projects.data ?? []).length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("myTracking.emptyProject")}
        </div>
      )}

      <ul className="mt-8 space-y-3">
        {(projects.data ?? []).map((project) => (
          <li key={project.id}>
            <Link
              to={`/my/tracking/${project.id}`}
              className="flex items-center justify-between rounded-2xl border border-border bg-card p-5 transition hover:bg-muted/30"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-lg font-medium">
                  <span className="truncate">{project.name}</span>
                  {project.archived && (
                    <Badge variant="secondary">{t("projectsList.archivedBadge")}</Badge>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {project.companyName}
                  {project.startDate
                    ? ` · ${t("myTracking.year", { year: Number(project.startDate.slice(0, 4)) })}`
                    : ""}
                </div>
              </div>
              <ArrowRight className="size-5 shrink-0 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
